import axios from 'axios';

/**
 * Le endpoint `/csrf-token` est exposé à la racine du host API (hors préfixe
 * `/api`). On déduit donc le host depuis VITE_API_BASE_URL, ou on retombe sur
 * l'origine courante (dev avec proxy Vite).
 */
const resolveBaseUrl = (): string => {
  const apiBaseUrl =
    typeof import.meta.env.VITE_API_BASE_URL === 'string'
      ? import.meta.env.VITE_API_BASE_URL.trim()
      : '';
  if (apiBaseUrl) {
    try {
      const url = new URL(apiBaseUrl);
      return `${url.protocol}//${url.host}`;
    } catch {
      // URL relative (ex. "/api") → on tombera sur l'origine.
    }
  }
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return 'http://localhost:3000';
};

const csrfClient = axios.create({
  baseURL: resolveBaseUrl(),
  withCredentials: true,
});

let csrfToken: string | null = null;
let pendingFetch: Promise<string | null> | null = null;

async function fetchToken(): Promise<string | null> {
  try {
    const { data } = await csrfClient.get<{ csrfToken?: string }>(
      '/csrf-token',
    );
    csrfToken = data?.csrfToken ?? null;
    return csrfToken;
  } catch {
    csrfToken = null;
    return null;
  } finally {
    pendingFetch = null;
  }
}

export async function ensureCsrfToken(): Promise<string | null> {
  if (csrfToken) {
    return csrfToken;
  }
  if (!pendingFetch) {
    pendingFetch = fetchToken();
  }
  return pendingFetch;
}

export async function refreshCsrfToken(): Promise<string | null> {
  if (!pendingFetch) {
    pendingFetch = fetchToken();
  }
  return pendingFetch;
}

export function resetCsrfToken() {
  csrfToken = null;
  pendingFetch = null;
}
